import { useCallback, useRef, useState } from 'react'
import { useDirtyGuard, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import InvoiceTemplatesList from './InvoiceTemplatesList'
import MailTemplates, { type MailTemplatesCtl } from './MailTemplates'
import { checkBeforeSave } from '../forms/checkForm'
import { PAGES_TEMPLATES_TEXTS as T } from '../../shared/texts'

// System → Vorlagen: every template in one place (operator, 2026-09-27: „Dann haben wir alle
// Vorlagen auf einen Fleck"). Two pills:
//   PDF    — the PDF templates (list + editor), until then Finanzen → PDF-Vorlagen. Module `invoices`.
//   E-Mail — mail texts, common mail frame, frame per brand, until then in Einstellungen. Module `settings`.
// Each pill shows only for a role that may read its module — the same rights as at the old places,
// and the server routes are unchanged (/api/invoice-templates: invoices; /api/mail-frames,
// /api/settings: settings). Editing in E-Mail needs settings:write, as before.
//
// Saved tab layouts: a tab stored as component 'invoice-templates' (the old Finanzen entry) opens
// this page on the PDF pill — see LayoutManager.tsx.

export type TemplatesPill = 'pdf' | 'mail'

export default function Templates({ tabId, initialPill }: { tabId?: string, initialPill?: TemplatesPill }) {
    // No <form> here (the mail frame has fields of its own, e.g. the logo width) — the page is checked.
    const pageRef = useRef<HTMLDivElement>(null)
    const { toast } = useToast()
    const { can } = useAuth()
    const canPdf = can('invoices', 'read')
    const canMail = can('settings', 'read')
    const mailReadOnly = !can('settings', 'write')
    const pills = [
        ...(canPdf ? [{ key: 'pdf' as const, label: T.pillPdf }] : []),
        ...(canMail ? [{ key: 'mail' as const, label: T.pillMail }] : []),
    ]
    const [pill, setPill] = useState<TemplatesPill>(() =>
        (initialPill && pills.some((p) => p.key === initialPill) ? initialPill : pills[0]?.key) ?? 'pdf')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')

    const mailCtl = useRef<MailTemplatesCtl | null>(null)
    const [mailDirty, setMailDirty] = useState(false)
    const bindMail = useCallback((ctl: MailTemplatesCtl | null) => {
        mailCtl.current = ctl
        setMailDirty(!!ctl?.dirty)
    }, [])
    const dirty = pill === 'mail' && mailDirty && !mailReadOnly

    async function save(): Promise<SaveResult> {
        const invalid = checkBeforeSave(pageRef.current, setError)
        if (invalid) return invalid
        setSaving(true); setError('')
        try {
            await mailCtl.current?.save()
            toast(T.saved)
            return { ok: true }
        } catch (e: any) {
            const message = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setError(message)
            return { ok: false, error: message }
        } finally {
            setSaving(false)
        }
    }
    useDirtyGuard(tabId, dirty, save)

    // Leaving the E-Mail pill would unmount what is typed — the same rule as the settings pills.
    function selectPill(p: TemplatesPill) {
        if (p === pill) return
        if (dirty) { toast(T.applyOrDiscardFirst, 'warn'); return }
        setPill(p)
    }

    if (!pills.length) return <p className="muted">{T.noAccess}</p>

    return (
        <div ref={pageRef} className="form-page">
            <div className="form-top">
                <div className="form-header">
                    <h1>{T.title}</h1>
                    {pill === 'mail' && !mailReadOnly && (
                        <>
                            <button type="button" disabled={saving || !dirty} onClick={() => void save()}>{saving ? '…' : T.apply}</button>
                            {dirty && (
                                <button type="button" className="secondary" disabled={saving} onClick={() => mailCtl.current?.discard()}>
                                    {T.discard}
                                </button>
                            )}
                        </>
                    )}
                </div>
                {pill === 'mail' && mailReadOnly && (
                    <div className="notice">{T.readOnlyNotice}</div>
                )}
                {error && <div className="notice err">{error}</div>}
                <div className="pill-tabs" role="tablist" aria-label={T.title}>
                    {pills.map((p) => {
                        const active = p.key === pill
                        const blocked = dirty && !active
                        return (
                            <button key={p.key} type="button" role="tab" className={`pill-tab${active ? ' active' : ''}`}
                                style={blocked ? { opacity: 0.45 } : undefined}
                                title={blocked ? T.pillBlocked : undefined}
                                onClick={() => selectPill(p.key)}>
                                {p.label}{active && dirty ? ' ●' : ''}
                            </button>
                        )
                    })}
                </div>
            </div>
            <div className="form-body">
                {pill === 'pdf' && canPdf && <InvoiceTemplatesList embedded />}
                {pill === 'mail' && canMail && <MailTemplates readOnly={mailReadOnly} bind={bindMail} />}
            </div>
        </div>
    )
}
